import { uniqBy } from 'lodash';

/**
 * @function
 * @name countDecimals
 * @description Подсчет десятичной части числа
 * @param number {Number}
 */
export const countDecimals = (number) => {
  if (Math.floor(number) === number) return 0;
  return number?.toString().split('.')[1] ? number?.toString().split('.')[1].length : 0;
};

/**
 * @function
 * @name countInteger
 * @description Подсчет целой части числа
 * @param number {Number}
 */
export const countInteger = (number) => (number?.toString().split('.')[0] ? number?.toString().split('.')[0].length : 0);

/**
 * @function
 * @name parseServiceList
 * @description Формирование листнига сервисов
 * @param servicesWithCategory {Array} - список сервисов
 * @param config {Object} - конфиг с доступами и настройками
 */
export const parseServiceList = (servicesWithCategory, config) => servicesWithCategory?.map((item) => ({
  ...item,
  services: item.services.map((service) => ({
    ...service,
    title: service.service_name,
    service_id: service.id,
    price: service.price,
    custom_price: service.price,
    custom_percent: '100',
    percent: '0',
    provider_rate_origin: service?.provider_rate_origin !== null ? Number(service?.provider_rate_origin).toFixed(2) : service?.provider_rate_origin,
    provider_rate:
        service?.provider_rate !== null
          ? Number(service?.provider_rate).toFixed(countDecimals(Number(config?.min_service_rate)))
          : service?.provider_rate,
  })),
}));

/**
 * @function
 * @name parseUserRates
 * @description Формирование custom rates списка
 * @param rates {Array} - список custom rates
 * @param services {Array} - список сервисов
 * @param searchValue {String} - значение посика
 * @param config {Object} - конфиг с доступами и настройками
 */
export const parseUserRates = (rates, services, searchValue, config) => {
  const findServices = rates.map((item) => {
    const findEl = services?.find((el) => el.id === Number(item.service_id));
    const maxDecimals = countDecimals(Number(config?.min_service_rate));

    return {
      ...item,
      ...findEl,
      price: findEl?.price,
      provider_rate_origin: findEl?.provider_rate_origin !== null ? Number(findEl?.provider_rate_origin).toFixed(maxDecimals) : findEl?.provider_rate_origin,
      provider_rate:
        findEl?.provider_rate !== null
          ? Number(findEl?.provider_rate).toFixed(maxDecimals)
          : findEl?.provider_rate,
      converted_price: item.percent === '0' ? Number(item.price) || 0 : (Number(findEl?.price) / 100) * item.price || 0,
      custom_price: item.percent === '0' ? item.price : parseInt(item.price, 10).toString() || '',
      custom_id: item.id,
    };
  });

  const categoryNames = uniqBy(
    services
      ?.filter((item) => findServices.find((service) => service.category_id === item.category_id))
      .map((el) => ({ category_name: el.category_name, category_id: el.category_id })),
    'category_id',
  );

  const ratesByCategories = categoryNames.reduce((acc, category) => {
    const filteredRates = findServices
      .filter((item) => item.category_id === category.category_id)
      .filter((item) => (item.service_name + item.service_id + item.category_name)
        .replace(/\s/g, '')
        .toLowerCase()
        .includes(searchValue.replace(/\s/g, '').toLowerCase()));

    const value = [
      filteredRates.length > 0 && { category_id: category.category_id, category_name: category.category_name, itCategory: true },
      ...filteredRates,
    ];
    return [...acc, ...value.filter(Boolean)];
  }, []);

  return ratesByCategories;
};

/**
 * @function
 * @name submitFormatValue
 * @description Формирования данных для отправки
 * @param values {Array}
 */
export const submitFormatValue = (values) => values.map((item) => ({
  service_id: item.service_id,
  price: item.price,
  percent: item.percent,
  rate_id: item.hasOwnProperty('category_name') ? '' : item.id,
}));

/**
 * @function
 * @name detectConvertedRate
 * @description Определение конвертации валюты
 * @param converted {Boolean} - была ли конвертация
 * @param provider_rate {String | Number} - значение провайдера
 * @param provider_rate_origin {String | Number} - оригинальной значение провайдера
 */
export const detectConvertedRate = (converted, provider_rate, provider_rate_origin) => {
  const diff = (a, b) => (a > b ? a - b : b - a);

  if (converted || diff(provider_rate, provider_rate_origin)) {
    return 'converted';
  }
  if (!converted && provider_rate !== null) {
    return 'not-converted';
  }
  return false;
};

/**
 * @function
 * @name onChangeRatesValue
 * @description Смена значения цены
 * @param value {String} - введенное значение
 * @param setFieldValue {Function} - функция установки значения поля
 * @param valueType {Number} - тип значения
 * @param setPrices {Function} - установка цены по типу
 */
export const onChangeRatesValue = (value, setFieldValue, valueType, setPrices) => {
  switch (valueType) {
  case '0':
    if (/^\d{0,17}\.?\d{0,4}$/.test(value)) {
      if (value[0] === '0' && value[1] !== '.' && value.length > 1) {
        setPrices((prev) => ({ ...prev, custom_price: value.substr(1) }));
        return setFieldValue(value.substr(1));
      }

      if (value === '' || countInteger(value) === 0) {
        setPrices((prev) => ({ ...prev, custom_price: value }));
        setFieldValue(value);
      } else {
        setPrices((prev) => ({ ...prev, custom_price: value }));
        setFieldValue(value);
      }
    }
    break;

  case '1':
    if (/^\d{0,8}$/.test(value)) {
      if (value[0] === '0' && value.length > 1) {
        setPrices((prev) => ({ ...prev, custom_percent: value.substr(1) }));
        return setFieldValue(value.substr(1));
      }

      if (value === '' || countInteger(value) === 0) {
        setPrices((prev) => ({ ...prev, custom_percent: value }));
        setFieldValue(value);
      } else {
        setPrices((prev) => ({ ...prev, custom_percent: value }));
        setFieldValue(value);
      }
    }
    break;

  default:
    break;
  }
};

/**
 * @function
 * @name onBlurValidateRate
 * @description Валидация и изменения значения цены при потери фокуса на элементе
 * @param e {Object} - объект события
 * @param setFieldValue {Function} - функция установки значения поля
 * @param valueType {Number} - тип значения
 * @param setPrices {Function} - установка цены по типу
 */
export const onBlurValidateRate = (e, setFieldValue, valueType, setPrices) => {
  const { value } = e.target;

  if (valueType === '1') {
    if (!value) {
      const validPercent = '0';
      setPrices((prev) => ({ ...prev, custom_percent: validPercent }));
      return setFieldValue(validPercent);
    }
  }

  if (valueType === '0') {
    if (!value) {
      const validPrice = '0.00';
      setPrices((prev) => ({ ...prev, custom_price: '0.00' }));
      return setFieldValue(validPrice);
    }

    if (value[0] === '.') {
      const validPrice = `0${value}`;
      setPrices((prev) => ({ ...prev, custom_price: validPrice }));
      return setFieldValue(validPrice);
    }

    if (countDecimals(value) === 0) {
      if (value[value.length - 1] === '.') {
        setPrices((prev) => ({ ...prev, custom_price: `${value}00` }));
        setFieldValue(`${value}00`);
      } else {
        if (countInteger(value) > 17) {
          setPrices((prev) => ({ ...prev, custom_price: `${value.slice(0, 17)}.00` }));
          setFieldValue(`${value.slice(0, 17)}.00`);
        } else {
          setPrices((prev) => ({ ...prev, custom_price: `${value}.00` }));
          setFieldValue(`${value}.00`);
        }
      }
    }

    if (value === '0.0000') {
      setPrices((prev) => ({ ...prev, custom_price: value.slice(0, -2) }));
      return setFieldValue(value.slice(0, -2));
    }

    if (countDecimals(value) === 1) {
      setPrices((prev) => ({ ...prev, custom_price: `${value}0` }));
      setFieldValue(`${value}0`);
    }

    if ((countDecimals(value) === 3 || countDecimals(value) === 4) && value[value.length - 1] === '0') {
      setPrices((prev) => ({ ...prev, custom_price: value.slice(0, -1) }));
      setFieldValue(value.slice(0, -1));
    }
  }
};

const calculatePriceOnPercent = (value, originalValue, config) => {
  const price = ((Number(originalValue || value) / 100) * value).toFixed(countDecimals(Number(config?.min_service_rate)));
  return price > config?.min_service_rate ? price : config?.min_service_rate;
}

/**
 * @function
 * @name generatePriceWithType
 * @description Генерация цены в зависимости от типа валюты
 * @param value {String} - введенное значение
 * @param valueType {Number} - тип значения
 * @param originalValue {String} - оригинальной значение
 */
export const generatePriceWithType = (value, valueType, originalValue, config) => ({
  custom_price: valueType === '0' ? value : calculatePriceOnPercent(value, originalValue, config) || '0.00',
  custom_percent: valueType === '0' ? parseInt((value / Number(originalValue || value)) * 100, 10) || '0' : parseInt(value, 10) || '0',
});
